import { mediaApi } from '../api/media';
import { postsApi } from '../api/posts';
import { getDraftBinary, removeCreationDraft } from '../utils/creationDrafts';

const STORAGE_KEY = 'amposter:video-publications:v1';
export const VIDEO_PUBLICATION_EVENT = 'amposter:video-publication-changed';

const running = new Set();

const readTasks = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
  catch (_) { return []; }
};

const writeTasks = (tasks) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks.slice(0, 5)));
  window.dispatchEvent(new CustomEvent(VIDEO_PUBLICATION_EVENT, { detail: tasks }));
};

const patchTask = (id, changes) => {
  const tasks = readTasks();
  const index = tasks.findIndex((task) => task.id === id);
  if (index < 0) return null;
  tasks[index] = { ...tasks[index], ...changes, updatedAt: new Date().toISOString() };
  writeTasks(tasks);
  return tasks[index];
};

export const getVideoPublicationTasks = () => readTasks();

export const getActiveVideoPublication = (ownerId) => readTasks().find((task) =>
  (!ownerId || task.ownerId === ownerId) && !['READY', 'FAILED'].includes(task.stage));

export const dismissVideoPublication = (id) => writeTasks(readTasks().filter((task) => task.id !== id));

export const cancelVideoPublication = async (id) => {
  const task = readTasks().find(item => item.id === id);
  if (!task || !['FAILED', 'REVIEW'].includes(task.stage) || running.has(id)) return;
  if (task.backendJobId) {
    try {
      const job = await mediaApi.get(task.backendJobId);
      if (!['READY', 'PROCESSING', 'CANCELLED'].includes(job.status)) await mediaApi.cancel(job.id);
    } catch (error) {
      if (error.status !== 404) throw error;
    }
  }
  // Keep the editable draft and local files; cancelling never discards the user's work.
  dismissVideoPublication(id);
};

export const approveVideoPublication = (id) => {
  const task = readTasks().find(item => item.id === id);
  if (!task || task.stage !== 'REVIEW') return;
  patchTask(id, { reviewApproved: true, stage: 'QUEUED' });
  void runTask(id);
};

const stageFromJob = (job) => {
  if (job.status === 'FAILED' && job.nextAttemptAt) return 'QUEUED';
  if (job.status === 'AWAITING_UPLOAD') return 'UPLOADING';
  if (job.status === 'UPLOADED') return 'QUEUED';
  if (job.status === 'PROCESSING') return 'ISOLATING';
  return job.status;
};

const uploadReservedVideo = async (job, file) => {
  return mediaApi.uploadReserved(job, file);
};

const runTask = async (taskId, suppliedFiles = {}) => {
  if (running.has(taskId)) return;
  running.add(taskId);
  try {
    let task = readTasks().find((item) => item.id === taskId);
    if (!task || ['READY', 'FAILED'].includes(task.stage)) return;

    const videoFile = suppliedFiles.videoFile || await getDraftBinary(task.ownerId, task.draftId, 'video');

    let job = null;
    if (task.backendJobId) {
      job = await mediaApi.get(task.backendJobId);
      if (job.status === 'AWAITING_UPLOAD' && !suppliedFiles.videoFile) {
        await mediaApi.cancel(job.id);
        job = null;
        patchTask(taskId, { backendJobId: null });
      }
    }

    if (!job) {
      if (!(videoFile instanceof Blob)) throw new Error('تعذر استعادة ملف الفيديو من المسودة.');
      patchTask(taskId, { stage: 'PREPARING', error: null });
      job = await mediaApi.reserve({
        type: 'VIDEO',
        fileName: task.fileName,
        contentType: task.contentType || 'video/mp4',
        sizeBytes: task.sizeBytes,
        durationSeconds: Math.max(1, Math.ceil(task.durationSeconds)),
        audioMode: task.audioMode || 'ORIGINAL',
      });
      patchTask(taskId, { backendJobId: job.id, stage: 'UPLOADING' });
    }

    if (job.status === 'AWAITING_UPLOAD') {
      if (!(videoFile instanceof Blob)) throw new Error('تعذر استعادة ملف الفيديو من المسودة.');
      patchTask(taskId, { stage: 'UPLOADING' });
      job = await uploadReservedVideo(job, videoFile);
    }

    if (job.status !== 'READY') {
      patchTask(taskId, { stage: job.status === 'PROCESSING' && task.audioMode !== 'ISOLATE' ? 'PROCESSING' : stageFromJob(job) });
      job = await mediaApi.waitUntilReady(job.id, {
        onStatus: (nextJob) => patchTask(taskId, { stage: nextJob.status === 'PROCESSING' && task.audioMode !== 'ISOLATE' ? 'PROCESSING' : nextJob.status === 'READY' ? 'QUEUED' : stageFromJob(nextJob) }),
      });
    }

    if (task.audioMode === 'ISOLATE' && !task.reviewApproved) {
      patchTask(taskId, { stage: 'REVIEW', previewUrl: job.publicUrl });
      return;
    }
    patchTask(taskId, { stage: 'PUBLISHING' });
    const coverFile = suppliedFiles.coverFile || await getDraftBinary(task.ownerId, task.draftId, 'video-cover');
    if (!(coverFile instanceof Blob)) throw new Error('تعذر استعادة غلاف الفيديو من المسودة.');
    const cover = await mediaApi.uploadImage(coverFile, 'video-thumbnail');
    const savedPost = await postsApi.create({
      ...task.postBody,
      type: 'VIDEO',
      draft: false,
      media: [
        { assetId: job.mediaAssetId, type: 'VIDEO', url: job.publicUrl, contentType: 'video/mp4', altText: task.postBody.title || null, cover: false },
        { assetId: null, type: 'IMAGE', url: cover.url, contentType: coverFile.type || 'image/jpeg', altText: task.postBody.title ? `غلاف ${task.postBody.title}` : 'غلاف الفيديو', cover: true },
      ],
    }, { allowDuringVideoProcessing: true });

    removeCreationDraft(task.ownerId, task.draftId);
    patchTask(taskId, { stage: 'READY', postId: savedPost.id, publicId: savedPost.publicId, backendJobId: job.id, error: null });
    window.dispatchEvent(new CustomEvent('postCreated', { detail: { postId: savedPost.id, post: savedPost } }));
  } catch (error) {
    console.error('Background video publication failed:', error);
    const friendly = {
      ISOLATION_QUEUE_FULL: 'عزل الصوت مشغول الآن. حاول لاحقاً أو اختر رفع الفيديو دون عزل.',
      ISOLATION_DAILY_LIMIT: 'استخدمت الحد التجريبي لعزل الصوت خلال آخر 24 ساعة. يمكنك الرفع دون عزل.',
      MEDIA_QUEUE_FULL: 'قائمة معالجة الفيديو ممتلئة الآن. حاول لاحقاً.',
      ACCESS_RESOURCE_NOT_FOUND: 'عزل الصوت متاح بإذن تجريبي فقط. يمكنك اختيار وضع صوت آخر.',
    };
    patchTask(taskId, { stage: 'FAILED', error: friendly[error?.code] || error?.message || 'تعذر نشر الفيديو.' });
  } finally {
    running.delete(taskId);
  }
};

export const startVideoPublication = ({ ownerId, draftId, videoFile, coverFile, durationSeconds, postBody, audioMode = 'ORIGINAL' }) => {
  if (getActiveVideoPublication(ownerId)) throw new Error('يوجد فيديو آخر قيد الرفع أو المعالجة.');
  const task = {
    id: crypto.randomUUID(), ownerId, draftId,
    fileName: videoFile.name || 'video.mp4',
    contentType: videoFile.type || 'video/mp4',
    sizeBytes: videoFile.size, durationSeconds, postBody, audioMode,
    stage: 'PREPARING', backendJobId: null, postId: null, error: null,
    createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
  };
  writeTasks([task, ...readTasks()]);
  void runTask(task.id, { videoFile, coverFile });
  return task;
};

export const resumeVideoPublications = (ownerId) => {
  readTasks()
    .filter((task) => task.ownerId === ownerId && !['READY', 'FAILED', 'REVIEW'].includes(task.stage))
    .forEach((task) => void runTask(task.id));
};
